import { DeleteFilled } from "@ant-design/icons";
import { Button, Col, Divider, Form, Input, Modal, Row, Select } from "antd";
import { Rule, RuleUIAction, RuleUICondition, RuleUIModel } from "models/rules";
import { Fragment, PropsWithChildren, useEffect, useState } from "react";
import { ActionMap } from "./Actions";
import { ConditionMap } from "./Conditions";

interface NewRuleModalProps {
    onRuleCreated: (rule: Rule) => Promise<void>;
}

const BlankSlateRule: RuleUIModel = {
    conditions: [],
    action: RuleUIAction.Unknown
};

const StyledDivider = ({ children }: PropsWithChildren) =>
    <Divider orientation="left" style={{ margin: '20px 0', lineHeight: '0px' }}>{children}</Divider>

const NewRuleModal = ({ onRuleCreated }: NewRuleModalProps) => {
    const [open, setOpen] = useState(false);
    const [rule, setRule] = useState<RuleUIModel>(() => ({ ...BlankSlateRule }));
    const [form] = Form.useForm<Rule>();

    // for some reason, the form doesn't properly reset after saving a rule, so reset the form
    // again when the modal is opened
    useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, form]);

    const addCondition = (id: number) => {
        setRule(cur => ({ ...cur, conditions: [...cur.conditions, id] }));
    }

    const setAction = (id: number) => {
        form.setFieldValue('action', undefined);
        setRule(cur => ({ ...cur, action: id }));
    }

    const removeCondition = (idx: number) => {
        rule.conditions.splice(idx, 1);

        setRule(cur => ({
            conditions: [...rule.conditions],
            action: rule.conditions.length === 0 ? RuleUIAction.Unknown : cur.action
        }));
    }

    const resetForm = () => {
        setRule({ ...BlankSlateRule });
        form.resetFields();
    }

    const closeAndReset = () => {
        resetForm();
        setOpen(false);
    }

    const hasConditions = rule.conditions.length > 0;
    const conflictingCondition = rule.conditions.some(c => c === RuleUICondition.Always);

    const conditionNodeList = (
        _: any,
        operation: {
            remove: (index: number | number[]) => void
        }
    ) => rule.conditions.map((c, idx) => {
        const ConditionNode = ConditionMap[c];
        const key = `condition-${crypto.randomUUID()}`;
        const deleteButton = <Button danger
            icon={<DeleteFilled />}
            onClick={() => {
                removeCondition(idx);
                operation.remove(idx);
            }}
            style={{ marginRight: '3px' }}
        />;

        if (idx + 1 === rule.conditions.length) {
            return <Fragment key={key}>
                {deleteButton}
                <ConditionNode index={idx} />
            </Fragment>
        }

        return <Fragment key={key}>
            {deleteButton}
            <ConditionNode index={idx} />
            <StyledDivider>And</StyledDivider>
        </Fragment>
    });

    const ActionNode = ActionMap[rule.action];

    return <>
        <Button type="primary" onClick={() => setOpen(true)}>Create New Rule</Button>
        <Modal
            title="Create New Rule"
            width='700px'
            open={open}
            onCancel={closeAndReset}
            onOk={async () => {
                const rule = await form.validateFields();
                await onRuleCreated(rule);
                closeAndReset();
            }}
            okButtonProps={{ disabled: !hasConditions || rule.action === RuleUIAction.Unknown }}
            closable={false}
            maskClosable={false}
            destroyOnClose
            keyboard={false}
        >
            <Row gutter={[4, 16]} style={{ marginBottom: '32px' }}>
                <Col span={3}>
                    <Button danger type="dashed" onClick={resetForm}>Reset</Button>
                </Col>

                <Col span={13}>
                    <Select
                        options={[
                            { label: "- Select a condition -", value: RuleUICondition.Unknown },
                            { label: "Always", value: RuleUICondition.Always, disabled: hasConditions },
                            { label: "When the title contains...", value: RuleUICondition.Contains, disabled: conflictingCondition },
                            { label: "When the title does not contain...", value: RuleUICondition.DoesNotContain, disabled: conflictingCondition },
                            { label: "When the title length is less than or equal to...", value: RuleUICondition.TitleLengthLtE, disabled: conflictingCondition },
                            { label: "When the title length is greater than or equal to...", value: RuleUICondition.TitleLengthGtE, disabled: conflictingCondition },
                        ]}
                        onChange={addCondition}
                        style={{ width: '100%' }}
                        value={0} />
                </Col>

                <Col span={8}>
                    <Select
                        options={[
                            { label: "- Select an action -", value: RuleUIAction.Unknown },
                            { label: "Insert At...", value: RuleUIAction.InsertAt, disabled: !hasConditions },
                            { label: "Remove...", value: RuleUIAction.Remove, disabled: !hasConditions },
                            { label: "Replace...", value: RuleUIAction.Replace, disabled: !hasConditions },
                        ]}
                        onChange={setAction}
                        style={{ width: '100%' }}
                        value={0} />
                </Col>
            </Row>
            <Row gutter={[32, 32]}>
                <Col span={24}>
                    <Form layout="inline" form={form} initialValues={{ conditions: undefined, action: undefined }}>
                        <Form.List name='conditions'>
                            {conditionNodeList}
                        </Form.List>
                        {rule.action !== RuleUIAction.Unknown && <>
                            {!conflictingCondition && <StyledDivider>Then</StyledDivider>}
                            <ActionNode />
                        </>}
                        <Form.Item name="ordinal" initialValue={0} hidden>
                            <Input />
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
        </Modal>
    </>
};

export default NewRuleModal;